/**
 * ModulesMosaic — Cockpit 3.0 top section.
 *
 * Tabbed mini-dashboards for every institutional module with live KPIs and
 * an actionable CTA. Hits /api/dashboard-cockpit/modules-mosaic in a single
 * call. Tabs are reorderable + hideable (persisted via dashboard-layout).
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, ArrowRight, EyeOff, Eye, GripVertical,
} from 'lucide-react';
import LiveSyncBadge from '../LiveSyncBadge';
import useSmartPolling from '../../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtValue = (kpi) => {
  if (kpi.fmt === 'currency') {
    const v = Number(kpi.value || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M ${kpi.currency || ''}`.trim();
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k ${kpi.currency || ''}`.trim();
    return `${v.toFixed(0)} ${kpi.currency || ''}`.trim();
  }
  return Number(kpi.value || 0).toLocaleString();
};

const TONE = {
  danger:  'text-rose-600',
  warning: 'text-amber-600',
  neutral: 'text-[#0F172A]',
};

export const ModulesMosaic = ({ layout, onLayoutChange }) => {
  const [tiles, setTiles] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/dashboard-cockpit/modules-mosaic`, auth);
      setTiles(r.data || []);
      if (!active && r.data?.length) setActive(r.data[0].code);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const { lastSync, refreshing, refresh } = useSmartPolling(load, {
    intervalMs: 30000, enabled: true,
  });

  // Apply layout (order + hidden) on top of fetched tiles
  const visibleTiles = useMemo(() => {
    const hidden = new Set(layout?.mosaic_hidden || []);
    const order  = layout?.mosaic_order || [];
    const byCode = Object.fromEntries(tiles.map(t => [t.code, t]));
    const ordered = order.map(c => byCode[c]).filter(Boolean);
    const rest = tiles.filter(t => !order.includes(t.code));
    return [...ordered, ...rest].filter(t => !hidden.has(t.code));
  }, [tiles, layout]);

  const activeTile = visibleTiles.find(t => t.code === active) || visibleTiles[0];

  const moveTile = (from, to) => {
    if (from === to) return;
    const allCodes = visibleTiles.map(t => t.code);
    const next = [...allCodes];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onLayoutChange({ ...layout, mosaic_order: next });
  };

  const toggleHidden = (code) => {
    const hidden = new Set(layout?.mosaic_hidden || []);
    hidden.has(code) ? hidden.delete(code) : hidden.add(code);
    onLayoutChange({ ...layout, mosaic_hidden: [...hidden] });
    if (active === code) setActive(null);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#0F172A] to-[#020617] rounded-xl p-6
        text-slate-400 flex items-center gap-2 mb-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading institutional cockpit…
      </div>
    );
  }

  return (
    <div className="mb-4 bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#020617]
      rounded-xl border border-slate-700/40 shadow-2xl overflow-hidden"
      data-testid="modules-mosaic">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A86A] font-semibold">
            Institutional Cockpit
          </span>
          <span className="text-[10px] text-slate-500">
            · {visibleTiles.length} live module{visibleTiles.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <LiveSyncBadge lastSync={lastSync} refreshing={refreshing}
            onRefresh={refresh} testId="cockpit-mosaic-live-sync" />
          <button onClick={() => setEditMode(v => !v)}
            className={`p-1.5 ${editMode ? 'text-[#C9A86A]' : 'text-slate-400 hover:text-white'}`}
            title={editMode ? 'Done editing' : 'Customize tabs'}
            data-testid="mosaic-edit-btn">
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-white/5">
        {visibleTiles.map((t, idx) => (
          <button key={t.code}
            draggable={editMode}
            onDragStart={(e) => e.dataTransfer.setData('idx', idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => moveTile(parseInt(e.dataTransfer.getData('idx'), 10), idx)}
            onClick={() => setActive(t.code)}
            data-testid={`mosaic-tab-${t.code}`}
            className={`relative px-3 py-2 text-xs font-semibold rounded-t-lg
              transition-all border-b-2 ${
              active === t.code
                ? 'bg-white/5 text-white border-[#C9A86A]'
                : 'text-slate-400 hover:text-white border-transparent'
            } ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
            style={active === t.code ? { color: t.color } : undefined}>
            <span>{t.title}</span>
            {editMode && (
              <span onClick={(e) => { e.stopPropagation(); toggleHidden(t.code); }}
                className="ml-2 text-slate-500 hover:text-rose-400 inline-block"
                title="Hide tab">
                <EyeOff className="w-3 h-3 inline" />
              </span>
            )}
          </button>
        ))}
        {/* Hidden tiles re-add menu */}
        {editMode && (layout?.mosaic_hidden || []).length > 0 && (
          <div className="flex items-center gap-1 ml-3 pb-2">
            {(layout.mosaic_hidden || []).map((code) => (
              <button key={code} onClick={() => toggleHidden(code)}
                className="px-2 py-1 text-[10px] text-slate-400 hover:text-white
                  border border-dashed border-slate-600 rounded">
                <Eye className="w-3 h-3 inline mr-1" />{code}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active tile body */}
      {activeTile && (
        <div className="p-5" data-testid={`mosaic-body-${activeTile.code}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em]"
                style={{ color: activeTile.color }}>
                {activeTile.code} · {activeTile.subtitle}
              </div>
              <h3 className="text-2xl font-serif text-white mt-1">{activeTile.title}</h3>
            </div>
            <Link to={activeTile.action.href}
              data-testid={`mosaic-deep-${activeTile.code}`}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                bg-white/5 hover:bg-white/10 text-white border border-white/10
                transition-colors">
              {activeTile.action.label}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {activeTile.kpis.map((kpi, i) => (
              <div key={i} className="rounded-lg bg-white/5 border border-white/10 p-3
                hover:bg-white/10 transition-colors"
                data-testid={`mosaic-kpi-${activeTile.code}-${i}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  {kpi.label}
                </div>
                <div className={`text-2xl font-bold tabular-nums mt-1
                  ${TONE[kpi.tone] || 'text-white'}`}>
                  {fmtValue(kpi)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulesMosaic;
